/**
 * HTML Sanitizer for rich text content
 *
 * Sanitizes user-edited HTML to only allow safe rich text tags.
 * Prevents XSS while preserving rich text formatting from contentEditable.
 */

/**
 * Allowed HTML tags for rich text content
 */
const ALLOWED_TAGS = ['br', 'span', 'strong', 'em', 'u', 'b', 'i'];

/**
 * Allowed attributes on tags (only style and class)
 */
const ALLOWED_ATTRS = ['style', 'class'];

/**
 * Sanitize HTML string to only allow safe rich text tags
 *
 * @param {string} html - Raw HTML string from contentEditable
 * @returns {string} Sanitized HTML with only allowed tags/attributes
 */
export function sanitizeHTML(html) {
  if (!html || typeof html !== 'string') return html || '';

  // If no HTML tags present, return as-is (plain text)
  if (!/<[^>]+>/.test(html)) return html;

  // Use DOMParser for robust parsing
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  function cleanNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const tagName = node.tagName.toLowerCase();

    // Process children first
    let childContent = '';
    node.childNodes.forEach(child => {
      childContent += cleanNode(child);
    });

    // If tag is not allowed, return just the children content (unwrap)
    if (!ALLOWED_TAGS.includes(tagName)) {
      return childContent;
    }

    // Build allowed attributes
    let attrs = '';
    ALLOWED_ATTRS.forEach(attrName => {
      const val = node.getAttribute(attrName);
      if (val) {
        // Sanitize style attribute - remove dangerous CSS
        let safeVal = val;
        if (attrName === 'style') {
          safeVal = val
            .replace(/javascript:/gi, '')
            .replace(/expression\s*\(/gi, '')
            .replace(/url\s*\(/gi, '')
            .replace(/@import/gi, '');
        }
        attrs += ` ${attrName}="${safeVal.replace(/"/g, '&quot;')}"`;
      }
    });

    // Self-closing tags
    if (tagName === 'br') {
      return '<br>';
    }

    // Skip empty spans (cleanup)
    if (tagName === 'span' && !attrs.trim() && childContent === '') {
      return '';
    }

    return `<${tagName}${attrs}>${childContent}</${tagName}>`;
  }

  let result = '';
  doc.body.childNodes.forEach(node => {
    result += cleanNode(node);
  });

  return result;
}

/**
 * Check if a string contains HTML tags
 * @param {string} str - String to check
 * @returns {boolean} True if string contains HTML tags
 */
export function containsHTML(str) {
  if (!str || typeof str !== 'string') return false;
  return /<[^>]+>/.test(str);
}

/**
 * Inline sanitizer function as a string for embedding in iframe script
 * This is the same logic as sanitizeHTML but written as a standalone function string
 * that can be injected into the iframe's JavaScript context.
 */
export const INLINE_SANITIZE_HTML = `
function sanitizeInlineHTML(html) {
  if (!html || typeof html !== 'string') return html || '';
  if (!/<[^>]+>/.test(html)) return html;

  var ALLOWED = ['br', 'span', 'strong', 'em', 'u', 'b', 'i'];
  var ALLOWED_ATTRS = ['style', 'class'];

  var parser = new DOMParser();
  var doc = parser.parseFromString(html, 'text/html');

  function cleanNode(node) {
    if (node.nodeType === 3) return node.textContent;
    if (node.nodeType !== 1) return '';

    var tag = node.tagName.toLowerCase();
    var children = '';
    for (var i = 0; i < node.childNodes.length; i++) {
      children += cleanNode(node.childNodes[i]);
    }

    if (ALLOWED.indexOf(tag) === -1) return children;

    var attrs = '';
    for (var j = 0; j < ALLOWED_ATTRS.length; j++) {
      var val = node.getAttribute(ALLOWED_ATTRS[j]);
      if (val) {
        var safe = val.replace(/javascript:/gi, '').replace(/expression\\s*\\(/gi, '').replace(/url\\s*\\(/gi, '').replace(/@import/gi, '');
        attrs += ' ' + ALLOWED_ATTRS[j] + '="' + safe.replace(/"/g, '&quot;') + '"';
      }
    }

    if (tag === 'br') return '<br>';
    if (tag === 'span' && !attrs.trim() && children === '') return '';

    return '<' + tag + attrs + '>' + children + '</' + tag + '>';
  }

  var result = '';
  for (var i = 0; i < doc.body.childNodes.length; i++) {
    result += cleanNode(doc.body.childNodes[i]);
  }
  return result;
}
`;

export default sanitizeHTML;
